import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Send, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

const inputCls =
  "w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm md:text-base text-ink placeholder:text-inksoft/50 focus:outline-none focus:ring-2 focus:ring-terra/40 focus:border-terra transition-[box-shadow,border-color] duration-200";

export const ContactForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, consent }),
      });
      if (!res.ok) throw new Error();
      toast.success(t.contact.success);
      setForm(EMPTY);
      setConsent(false);
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="py-24 lg:py-32 bg-cream border-t border-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="text-xs md:text-sm font-semibold uppercase tracking-[0.22em] text-terra mb-6"
          >
            {t.contact.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif font-medium tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            {t.contact.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-inksoft"
          >
            {t.contact.sub}
          </motion.p>
        </div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 bg-white border border-line rounded-3xl p-7 md:p-10 shadow-[0_20px_60px_rgba(45,50,43,0.07)]"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="contact-name" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.nameLabel}</label>
              <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={t.contact.namePlaceholder} className={inputCls} />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.emailLabel}</label>
              <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder={t.contact.emailPlaceholder} className={inputCls} />
            </div>
            <div>
              <label htmlFor="contact-phone" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.phoneLabel}</label>
              <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder={t.contact.phonePlaceholder} className={inputCls} />
            </div>
            <div>
              <label htmlFor="contact-subject" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.subjectLabel}</label>
              <select id="contact-subject" data-testid="contact-subject-select" required value={form.subject} onChange={set("subject")} className={inputCls}>
                <option value="" disabled>{t.contact.subjectPlaceholder}</option>
                {t.contact.subjectOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-5">
            <label htmlFor="contact-message" className="block text-xs font-semibold uppercase tracking-widest text-inksoft mb-2">{t.contact.messageLabel}</label>
            <textarea id="contact-message" data-testid="contact-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder={t.contact.messagePlaceholder} className={`${inputCls} resize-y`} />
          </div>
          <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="contact-consent-label">
            <input
              type="checkbox"
              required
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 shrink-0 accent-[#C86A51]"
              data-testid="contact-consent-checkbox"
            />
            <span className="text-sm leading-relaxed text-inksoft">
              {t.contact.consentText}{" "}
              <Link to="/privacy" data-testid="contact-privacy-link" className="text-terra font-medium underline underline-offset-2">Privacy Policy</Link>
            </span>
          </label>
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="mt-7 inline-flex items-center gap-2 bg-terra hover:bg-terradark disabled:opacity-60 text-cream text-sm font-semibold px-8 py-3.5 rounded-full transition-colors duration-300"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2} />}
            {sending ? t.contact.sending : t.contact.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
